import { Injectable } from '@angular/core';
import { SelectItem, SelectItemClient } from 'Nswag/nswag-api-sps';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { SpSelectItems } from 'app/core/Enums/SpSelectItems';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { VacancyClient } from 'Nswag/nswag-api-pimmaJob';

export interface SpProvider {
  [spName: string]: ReplaySubject<SelectItem[]>;
}

@Injectable()
export class SpService {
  spRegion: SelectItem[] = [];
  spDistrict: SelectItem[] = [];
  spInstitution: SelectItem[] = [];
  spDepartment: SelectItem[] = [];
  spPosition: SelectItem[] = [];
  spSubjects: SelectItem[] = [];
  spTypePrezidentSchools: SelectItem[] = [];
  spPedagogicalCategories: SelectItem[] = [];
  spEducationLevels: SelectItem[] = [];
  spTypeOfPositions: SelectItem[] = [];
  spCategoryOfPositions: SelectItem[] = [];
  spEducationalOrganizations: SelectItem[] = [];
  spLanguages: SelectItem[] = [];
  spApplicantFileTypes: SelectItem[] = [];
  spGenders: SelectItem[] = [];

  private downloadedSps: string[] = [];

  provider: SpProvider = {};
  initialDownloadSps: SpSelectItems[];

  constructor(
    private selectItemClient: SelectItemClient,
    private vacancyClient: VacancyClient,
  ) {
    this.initialDownloadSps = [
      SpSelectItems.SPRegions,
      SpSelectItems.SPDistricts,
      SpSelectItems.SPInstitutions,
      SpSelectItems.SpSubjects,
      SpSelectItems.SpTypePrezidentSchools,
      SpSelectItems.SpEducationLevels,
      SpSelectItems.SpEducationalOrganization,
      SpSelectItems.SpTypeOfPosition,
      SpSelectItems.SpCategoryOfPosition,
      SpSelectItems.SpLanguage,
      SpSelectItems.SPPedagogicalCategories,
      SpSelectItems.SpApplicantFileTypes,
      SpSelectItems.SpGenders,
    ];

    for (const init of this.initialDownloadSps)
      this.provider[init] = new ReplaySubject(1);

    this.Initialize(this.initialDownloadSps);

    //this.initializeVacancySp();
  }
  // public initializeVacancySp(): void {
  //   this.vacancyClient.getDepartments(this.vacancyService.vacancySource).subscribe({
  //     next: vacancy => {
  //       this.spDepartment = vacancy as [];
  //     },
  //   });

  //   this.vacancyClient.getPositions(this.vacancyService.vacancySource).subscribe({
  //     next: position => {
  //       this.spPosition = position as [];
  //     },
  //   });
  // }

  public Initialize(
    initialDownloadSps: SpSelectItems[] = this.initialDownloadSps
  ) {
    from(initialDownloadSps)
      .pipe(
        mergeMap((sp, index) => {
          return this.updateSp(sp).pipe(
            catchError((err: HttpErrorResponse) => {
              console.error(
                'Error downloading sp from api. Sp: ' + sp.toString(),
                err
              );
              return of(new Array<SelectItem>(0));
            }),
            map(items => ({
              items: items,
              sp: sp,
              index: index,
            }))
          );
        })
      )
      .subscribe(data => {
        this.provider[data.sp].next(data.items);

        if (!environment.production) {
        }
      });
  }

  getSpDistrict(regionId: string) {
    if (!regionId) return;

    this.selectItemClient
      .getSelectItemsByRoot(SpSelectItems.SPDistricts, regionId)
      .subscribe(
        item =>
          (this.spDistrict = item.map(x => {
            x.value = x.code;
            return x;
          }))
      );
  }

  getLabelByValue(selectItem: SelectItem[], value: any): string {
    if (!selectItem || selectItem.length < 1) return '';

    return selectItem.find(f => f.value == <string>value)?.label ?? '';
  }

  public updateSp(sp: SpSelectItems): Observable<SelectItem[]> {
    switch (sp) {
      case SpSelectItems.SPPedagogicalCategories:
        return this.downloadSp(
          SpSelectItems.SPPedagogicalCategories,
          this.spPedagogicalCategories
        );

      case SpSelectItems.SPRegions:
        return this.downloadSp(
          SpSelectItems.SPRegions,
          this.spRegion,
          null,
          true
        );

      case SpSelectItems.SPDistricts:
        return this.downloadSp(
          SpSelectItems.SPDistricts,
          this.spDistrict,
          null,
          true
        );
      case SpSelectItems.SPInstitutions:
        return this.downloadSp(
          SpSelectItems.SPInstitutions,
          this.spInstitution
        );
      case SpSelectItems.SpSubjects:
        return this.downloadSp(SpSelectItems.SpSubjects, this.spSubjects);
      case SpSelectItems.SpTypePrezidentSchools:
        return this.downloadSp(
          SpSelectItems.SpTypePrezidentSchools,
          this.spTypePrezidentSchools,
          t => !!t.value
        );
      case SpSelectItems.SpEducationLevels:
        return this.downloadSp(
          SpSelectItems.SpEducationLevels,
          this.spEducationLevels
        );
      case SpSelectItems.SpCategoryOfPosition:
        return this.downloadSp(
          SpSelectItems.SpCategoryOfPosition,
          this.spCategoryOfPositions
        );
      case SpSelectItems.SpTypeOfPosition:
        return this.downloadSp(
          SpSelectItems.SpTypeOfPosition,
          this.spTypeOfPositions
        );
      case SpSelectItems.SpLanguage:
        return this.downloadSp(SpSelectItems.SpLanguage, this.spLanguages);
      case SpSelectItems.SpEducationalOrganization:
        return this.downloadSp(
          SpSelectItems.SpEducationalOrganization,
          this.spEducationalOrganizations
        );
      case SpSelectItems.SpApplicantFileTypes:
        return this.downloadSp(
          SpSelectItems.SpApplicantFileTypes,
          this.spApplicantFileTypes
        );
      case SpSelectItems.SpGenders:
        return this.downloadSp(
          SpSelectItems.SpGenders,
          this.spGenders
        );
    }
  }

  public getSpSelectItems(sp: SpSelectItems): SelectItem[] {
    switch (sp) {
      case SpSelectItems.SPRegions:
        return this.spRegion;
      case SpSelectItems.SPDistricts:
        return this.spDistrict;
      case SpSelectItems.SPInstitutions:
        return this.spInstitution;
      case SpSelectItems.SpSubjects:
        return this.spSubjects;
      case SpSelectItems.SpTypePrezidentSchools:
        return this.spTypePrezidentSchools;
      case SpSelectItems.SpEducationLevels:
        return this.spEducationLevels;
      case SpSelectItems.SpTypeOfPosition:
        return this.spTypeOfPositions;
      case SpSelectItems.SpCategoryOfPosition:
        return this.spCategoryOfPositions;
      case SpSelectItems.SpEducationalOrganization:
        return this.spEducationalOrganizations;
      case SpSelectItems.SpApplicantFileTypes:
        return this.spApplicantFileTypes;
        case SpSelectItems.SpGenders:
        return this.spGenders;
    }
  }

  public downloadSp(
    sp: SpSelectItems,
    property: SelectItem[],
    filterP?: (item: SelectItem) => boolean,
    isCodeValue?: boolean,
    downloadBy?: 'original' | 'code' | 'rootCode',
    codeId?: string,
    sliceFirst?: boolean,
    mapP?: (item: SelectItem) => SelectItem,
    sortP?: (first: SelectItem, second: SelectItem) => number
  ): Observable<SelectItem[]> {
    let observable: Observable<SelectItem[]>;
    switch (downloadBy) {
      case 'code':
        observable = this.selectItemClient.getSelectItemsByCode(sp, codeId);
        break;

      case 'rootCode':
        observable = this.selectItemClient.getSelectItemsByRoot(sp, codeId);
        break;
      default:
        observable = this.selectItemClient.getSelectItems(sp);
        break;
    }

    return observable.pipe(
      map(items => {
        if (!environment.production) {
          if (!this.downloadedSps.includes(sp)) {
            this.downloadedSps.push(sp);          }        }

        let localValue: SelectItem[] = [];

        if (filterP) localValue = items.filter(f => filterP(f));
        else localValue = sliceFirst ? items.slice(1) : items;

        if (isCodeValue)
          localValue = localValue.map(m => {
            m.value = m.code;
            return m;
          });

        if (mapP) localValue = localValue.map(v => mapP(v));

        if (sortP) localValue = localValue.sort((f, s) => sortP(f, s));

        property.splice(0, property.length - 1);
        localValue.forEach(x => property.push(x));
        return [...property];
      })
    );
  }
}
